export const VISION_KINDS_ALL = ['light_multiplier', 'dark_monochrome', 'sense_all'];
/** Whether a kind has a range: every kind but a light multiplier, which only stretches lights. */
export const hasRange = (kind) => kind !== 'light_multiplier';
/** `entries`: the most senses a token carries; `range`: the longest, in squares (120, 600 ft — a light's longest too). */
export const VISION_LIMITS = { entries: 8, range: 120, multiplier: 10 };
const KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
const isKey = (v) => typeof v === 'string' && KEY_RE.test(v);
const isLabel = (v) => typeof v === 'string' && v.trim().length > 0 && v.length <= 60;
const isKind = (v) => VISION_KINDS_ALL.includes(v);
const isFactor = (v) => typeof v === 'number' && Number.isFinite(v) && v > 1 && v <= VISION_LIMITS.multiplier;
const half = (v) => Math.round(v * 2) / 2;
/**
 * `features.combat.vision`, strictly: an array of `{ key, kind, label }`, a light multiplier with its `multiplier`
 * (above 1) and no other kind with one, keys unique, at most `VISION_LIMITS.entries`. Anything else is none — nothing
 * defaulted (a creature then has normal sight, §8).
 */
export function parseVisionRules(v) {
    if (!Array.isArray(v) || v.length > VISION_LIMITS.entries)
        return [];
    const out = [];
    for (const e of v) {
        if (typeof e !== 'object' || e === null || Array.isArray(e))
            return [];
        const { key, kind, label, multiplier } = e;
        if (!isKey(key) || !isKind(kind) || !isLabel(label))
            return [];
        if (kind === 'light_multiplier' ? !isFactor(multiplier) : multiplier !== undefined)
            return [];
        out.push({ key, label: label.trim(), kind, ...(kind === 'light_multiplier' ? { multiplier: multiplier } : {}) });
    }
    return new Set(out.map((r) => r.key)).size === out.length ? out : [];
}
/** One sense, strictly: a key, a kind, a range where the kind has one (to half a square, 0 < range ≤ 120) and null where not. */
export function parseSense(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const { key, kind, range, multiplier } = v;
    if (!isKey(key) || !isKind(kind))
        return null;
    if (kind === 'light_multiplier' ? !isFactor(multiplier) : multiplier !== undefined)
        return null;
    if (!hasRange(kind))
        return range === null ? { key, kind, range: null, multiplier: multiplier } : null;
    if (typeof range !== 'number' || !Number.isFinite(range) || range <= 0 || range > VISION_LIMITS.range)
        return null;
    const r = half(range);
    return r > 0 ? { key, kind, range: r } : null;
}
/** A token's senses, strictly: an array of at most 8, keys unique. Empty is none. */
export function parseSenses(v) {
    if (!Array.isArray(v) || v.length > VISION_LIMITS.entries)
        return null;
    const out = [];
    for (const e of v) {
        const s = parseSense(e);
        if (!s)
            return null;
        out.push(s);
    }
    return new Set(out.map((s) => s.key)).size === out.length ? out : null;
}
export const copySenses = (s) => s.map((x) => ({ ...x }));
/**
 * The server's stamp (`object.vision` at commit): each sense's kind and factor read from the table's system by its key,
 * whatever the wire said. A string when a key is not one of the system's (or the system declares no vision).
 */
export function stampSenses(senses, rules) {
    const out = [];
    for (const s of senses) {
        const rule = rules.find((r) => r.key === s.key);
        if (!rule)
            return rules.length ? 'That is not a kind of vision this game system has.' : "This table's game system declares no kinds of vision.";
        if (hasRange(rule.kind) !== (s.range !== null))
            return `${rule.label} ${hasRange(rule.kind) ? 'needs' : 'has no'} range.`;
        out.push({ key: s.key, kind: rule.kind, range: s.range, ...(rule.multiplier !== undefined ? { multiplier: rule.multiplier } : {}) });
    }
    return out;
}
const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/**
 * A creature's senses out of its stat block's text (§8; "Darkvision 60 ft., low-light vision"): each of the system's
 * vision LABELS found as words, case-insensitively, with the range that follows it in the system's distance unit
 * ("60 ft.") converted to squares by `per_square`. A kind with a range is taken only with its range; a kind without one
 * only without. Nothing named is none — never a guess from the creature's type. The first mention of each key counts.
 */
export function visionFromText(texts, rules, distance) {
    const out = [];
    for (const rule of rules) {
        if (hasRange(rule.kind) && !distance)
            continue;
        const unit = distance ? escape(distance.unit) : '';
        const re = new RegExp(`(?<![\\p{L}\\p{N}-])${escape(rule.label).replace(/[\s-]+/g, '[\\s-]+')}(?![\\p{L}-])(?:\\s+(\\d{1,4})\\s*${unit}\\.?(?![\\p{L}]))?`, 'iu');
        for (const text of texts) {
            const m = re.exec(text);
            if (!m)
                continue;
            if (hasRange(rule.kind)) {
                if (m[1] === undefined)
                    continue;
                const range = half(Number(m[1]) / distance.perSquare);
                if (range <= 0 || range > VISION_LIMITS.range)
                    continue;
                out.push({ key: rule.key, kind: rule.kind, range });
            }
            else {
                if (m[1] !== undefined)
                    continue;
                out.push({ key: rule.key, kind: rule.kind, range: null, multiplier: rule.multiplier });
            }
            break;
        }
    }
    return out.slice(0, VISION_LIMITS.entries);
}
/**
 * A character's senses from its sheet (issue 1338; design/30 §8): each kind the sheet gives, as the FIRST of the
 * system's vision rules of that kind (its key, and a light multiplier's factor), a range converted to squares by the
 * system's `per_square` to half a square as a stat block's is (`visionFromText`). A kind the system does not declare
 * is skipped, a range with no distance rule too; a range of none or past `VISION_LIMITS.range` is dropped.
 */
export function visionFromSheet(senses, rules, distance) {
    const out = [];
    for (const kind of VISION_KINDS_ALL) {
        const rule = rules.find((r) => r.kind === kind);
        if (!rule)
            continue;
        if (kind === 'light_multiplier') {
            if (senses.light_multiplier === true)
                out.push({ key: rule.key, kind, range: null, multiplier: rule.multiplier });
            continue;
        }
        const given = senses[kind];
        if (given === undefined || !distance || !(distance.perSquare > 0))
            continue;
        const range = half(given / distance.perSquare);
        if (!(range > 0) || range > VISION_LIMITS.range)
            continue;
        out.push({ key: rule.key, kind, range });
    }
    return out;
}
/** Whether two sets of senses are the same, in any order. */
export function sameSenses(a, b) {
    const norm = (s) => JSON.stringify(s.map((x) => [x.key, x.kind, x.range, x.multiplier ?? null]).sort());
    return norm(a) === norm(b);
}
export function summarise(senses) {
    let multiplier = 1;
    let dark = 0;
    let all = 0;
    for (const s of senses ?? []) {
        if (s.kind === 'light_multiplier')
            multiplier = Math.max(multiplier, s.multiplier ?? 1);
        else if (s.kind === 'dark_monochrome')
            dark = Math.max(dark, s.range ?? 0);
        else
            all = Math.max(all, s.range ?? 0);
    }
    return { multiplier, dark, all, key: `${multiplier}|${dark}|${all}` };
}
/**
 * The stat block's rows a creature's senses are read from: every text row but one about its attacks, where a sense
 * named is something it does to others ("rage powers (low-light vision, …)", a Pathfinder special attack), not one
 * it has. Matched on the row's label, as the stat block shows it.
 */
export const senseRows = (stats) => stats.filter((s) => !/attack/i.test(s.label)).map((s) => s.value);
//# sourceMappingURL=vision.js.map